// The Zip All timeline: a rolling start that winds up and then pours into the
// folder. Pure functions of elapsed seconds, kept apart from the component for
// the same reason the geometry is — the render loop is not the only caller, and
// a timeline you can ask questions about is one you can test.
//
//   roll  the helix is already turning at its ambient speed when you arrive
//   wind  it accelerates, which is the part that tells you something is happening
//   pour  the cylinder collapses inward and downward into the folder
//   rest  the folder sits there holding everything
//
// Acceleration is eased IN (cubic), not linear. A linear ramp reads as a machine
// being driven; easing in reads as something being drawn, which is the difference
// between a progress bar and a cyclone.

// Short on purpose (Robert 2026-08-01: "start much quicker"). The first cut spent
// nearly four seconds winding up, which is a long time to watch something not
// happen yet — the wind-up has to read as intent, not as a delay.
//
// `lift` is the opening move: the photos are still sitting in their places on the
// Files tab and then leave it for the cylinder.
//
// Retimed 2026-08-01 after watching it: "they all burst to the helix and then
// drain... the start of the drain needs to be faster". The whole thing is now
// under three seconds — burst out, a beat of turning, then away into the folder.
// Nothing here should ever be long enough that a person waits for it: the zip it
// covers takes 0.04s for a 13-file claim and about a second for the worst claim
// in the database, so the animation is the slow part, not the work.
export const TIMELINE = { lift: 0.55, roll: 0.12, wind: 0.75, pour: 0.8, rest: 0.55 };

// How early the FIRST cards start leaving, before the pour proper. The lowest on
// the cylinder go during the wind-up, so the folder is taking files — and the
// counter is moving — from almost the moment the cylinder forms, instead of
// sitting on zero for the whole 1.4s of burst and spin (Robert 2026-08-01).
//
// The count stays honest: a card that has arrived really is in the archive. What
// it must NOT become is a counter running ahead of the cards — the zip itself
// finishes in about 40ms, so every number on screen is paced by the animation,
// and it is only truthful because it tracks what you can actually see land.
export const EARLY = 0.5;

export const LIFT_END = TIMELINE.lift;
export const WIND_END = TIMELINE.lift + TIMELINE.roll + TIMELINE.wind;
export const POUR_END = WIND_END + TIMELINE.pour;
export const TOTAL = POUR_END + TIMELINE.rest;

const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
const easeIn = (x) => x * x * x;
const easeOut = (x) => 1 - (1 - x) ** 3;

/**
 * WAITING FOR SLOW WORK.
 *
 * The point the sequence will wait at if the archive is not ready: the instant
 * before the pour, when the helix is turning at full speed. Everything up to here
 * is unconditional, because the burst and the wind-up are the same however long
 * the work takes; everything after it is the ending, which must not begin until
 * there is something to end with.
 */
// Moved back by EARLY when the leaders started going before the pour: the wait
// has to begin before ANY card has left, or a slow archive freezes the first few
// part-way to the folder and they hang in the air while the rest spins on. The
// test suite caught exactly that.
export const HOLD_AT = WIND_END - EARLY;

/**
 * Maps real elapsed seconds to SEQUENCE seconds, pausing at the hold point until
 * the work is done.
 *
 * Holding rather than slowing down, deliberately. Stretching the whole thing to
 * fit a slow zip drags out the burst — the part with all the life in it — and
 * makes a fast claim feel sluggish for no reason. Pausing at full spin costs
 * nothing visually, because the cylinder keeps turning: the render loop
 * integrates travel against REAL time, so only the sequence clock stops. It reads
 * as the machine working, which is what is actually happening.
 *
 * `releasedSeconds` is when the work finished, measured from the same start.
 * Infinity means still working, 0 means it was ready before we asked.
 */
export function sequenceClock(elapsed, releasedSeconds = 0, pourStretch = 1) {
  if (elapsed <= HOLD_AT) return elapsed;
  const heldUntil = Math.max(HOLD_AT, releasedSeconds);
  if (elapsed < heldUntil) return HOLD_AT;
  // Past the hold the clock can run SLOW, which is how a big archive earns a
  // longer ending without a second timeline to keep in step: every phase function
  // still works in sequence seconds and knows nothing about it.
  return HOLD_AT + (elapsed - heldUntil) / pourStretch;
}

/**
 * How much longer a big archive's ending runs. Robert's rule (2026-08-01): half a
 * second per fifty files, capped at two and a half — "if they are zipping 600
 * files, i dont think they can complain about 2.5 secs extra".
 *
 * CONTINUOUS rather than stepped at each fiftieth file, so 49 files and 51 files
 * do not visibly differ for no reason a viewer could name.
 */
export const BONUS_CAP = 2.5;
export const bonusSeconds = (fileCount) => Math.min(BONUS_CAP, Math.max(0, fileCount) * 0.01);

/**
 * The extra time goes into the POUR and the folder's beat, never the burst.
 *
 * The burst is the reveal and reads the same whether it is throwing three files
 * or six hundred; stretching it just makes a big claim feel slow to start. The
 * pour is where the volume actually shows, and it is where the counter runs — at
 * 571 files the count was stepping fourteen at a time through a 0.8s pour, which
 * is far too fast to read. Giving that stretch to the ending buys legibility as
 * well as weight, and leaves the folder holding the final number for longer.
 */
export const pourStretchFor = (fileCount) => 1 + bonusSeconds(fileCount) / (TOTAL - HOLD_AT);

/**
 * The cylinder's radius over the sequence, as a fraction of full size: it opens
 * out as the files arrive, then closes slowly on itself while it turns.
 *
 * This is what makes the moment read as a VORTEX rather than a gallery. The
 * component this was ported from is an ambient, infinite scroll — it descends and
 * wraps forever because it has to. Ours has a beginning and an end, and borrowing
 * the endless descent cost more than it gave: with a full cylinder the individual
 * downward motion is invisible (every card that leaves the bottom is replaced at
 * the top, so the shape never changes), while with only a few files it is all you
 * see. Robert 2026-08-01: "the helix which we copied does not work... its more
 * expand out and then circle where it is slowly collapsing in on itself".
 *
 * So the cards hold their heights and the whole cylinder turns and tightens. What
 * you read is the thing gathering itself up, which is what is actually happening.
 */
export function radiusScaleAt(t, { arrive = 0.5, settle = 0.62 } = {}) {
  if (t <= 0) return arrive;
  if (t < LIFT_END) return arrive + (1 - arrive) * easeOut(clamp01(t / LIFT_END));
  if (t >= WIND_END) return settle;
  // LINEAR, and both curves were wrong before it. Eased IN left the first half of
  // the wind-up visually static, so it read as "spin, then close". Eased OUT
  // overcorrected the other way: it dropped to a smaller circle inside about two
  // tenths of a second and then crept, which reads as "step down, spin, then
  // close" — the same two-part motion with the parts swapped. A constant rate is
  // the only one that is actually "a gradual close from the start" (Robert
  // 2026-08-01), because it is the only one with no fast bit and no slow bit.
  return 1 - (1 - settle) * clamp01((t - LIFT_END) / (WIND_END - LIFT_END));
}

/**
 * How far a card has left the page for the cylinder: 0 = sitting exactly on its
 * thumbnail, 1 = on the helix.
 *
 * They BURST — near enough all at once (Robert 2026-08-01). The first cut walked
 * them off the page one at a time in reading order, which was pretty and cost far
 * too much of the runtime doing nothing. The stagger left is only enough to keep
 * the departure from looking mechanical.
 *
 * Eased OUT, not in-out: fast off the mark and gentle on arrival, which is what
 * "burst" means. An eased-in start makes them creep away from the page first.
 */
export function liftAt(t, cardIndex, cardCount, { stagger = 0.12 } = {}) {
  if (t <= 0) return 0;
  const delay = cardCount <= 1 ? 0 : (cardIndex / (cardCount - 1)) * stagger;
  // 0.95 of the window, so the last card is safely home before the phase ends
  // rather than landing on a floating-point boundary.
  return easeOut(clamp01((t - delay) / ((TIMELINE.lift - stagger) * 0.95)));
}

/**
 * How dark the backdrop is, 0 (the Files tab showing through, untouched) to 1.
 *
 * The clones start life pixel-aligned on top of the real thumbnails against a
 * fully transparent backdrop, so the first frame is invisible — nothing flashes,
 * nothing jumps. The dark closes in only as the photos leave, which is what
 * covers the now-empty page behind them. Reaching full black slightly before the
 * lift ends keeps the last stragglers from being seen against the real page.
 */
export function backdropAt(t) {
  return easeOut(clamp01(t / (TIMELINE.lift * 0.8)));
}

/**
 * Rotation, in REVOLUTIONS PER SECOND. Rises from an ambient drift to `top` across
 * roll+wind, then HOLDS through the pour — the cards are still turning while they
 * collapse, so the vortex keeps its twist instead of freezing into a funnel.
 *
 * The unit changed when the cards stopped descending, and these numbers did not
 * follow it. It used to be SLOTS per second, divided by the slot count to get an
 * angle — so 3.4 meant 0.34 rev/s on a full cylinder. Read straight as
 * revolutions it became 3.4 rev/s, ten times faster, and it also stopped varying
 * with the file count (which it never should have). Retuned 2026-08-01 to land
 * near the old feel: halving 3.4 was the ask, but halving a number that is ten
 * times too big only gets you five times too big.
 */
// HALVED 2026-08-02 (top 0.42 -> 0.21). The wind-up reaches full speed before the
// first card leaves and then HOLDS there for as long as the archive takes — so on
// anything but a fast line this is not a moment, it is the whole wait, spinning
// under a percentage that is barely moving. A revolution every 2.4s reads as
// urgent for two seconds and frantic for thirty. At 0.21 it is one turn per five
// seconds: still plainly working, calm enough to watch.
//
// `ambient` is untouched. It only applies during the lift, where it is already a
// revolution every twenty seconds — the cards are flying off the page there and
// the cylinder is meant to drift, not turn.
export const SPIN = { ambient: 0.05, top: 0.21 };

export function speedAt(t, { ambient = SPIN.ambient, top = SPIN.top } = {}) {
  if (t >= HOLD_AT) return top;
  // Measured from when the cylinder exists (not from the click — during the lift
  // the cards are still flying in and should drift, not tear round) and finished
  // by HOLD_AT rather than WIND_END. The wind-up has to be COMPLETE before the
  // first card leaves and before the sequence can pause to wait for a slow
  // archive, or a long wait sits at half speed mid-accelerate.
  return ambient + (top - ambient) * easeIn(clamp01((t - LIFT_END) / (HOLD_AT - LIFT_END)));
}

/**
 * How far a card has been pulled in, 0 (on the cylinder) to 1 (inside the folder).
 *
 * Staggered by where the card sits on the strand, NOT by its index: the ones
 * nearest the bottom go first, so the helix drains like something pouring out of
 * the bottom of a funnel. Staggering by index instead makes the whole cylinder
 * implode at once, which reads as a crossfade.
 */
export function collapseAt(t, cardProgress, { stagger = 0.22 } = {}) {
  const local = t - (WIND_END - EARLY);
  if (local <= 0) return 0;
  // Spread across the early window as well, so the leaders trickle rather than
  // leaving in a clump: the bottom of the cylinder goes first and the top waits
  // for the pour proper.
  const delay = (1 - cardProgress) * (stagger + EARLY);
  const window = (TIMELINE.pour - stagger) * 0.92;
  return easeIn(clamp01((local - delay) / window));
}

/**
 * Overlay opacity on the way out: 1 until the folder has had its beat, then down
 * to 0 so the Files tab dissolves back in rather than being snapped back to.
 *
 * The sequence closes itself (Robert 2026-08-01) — no bar, no buttons, no
 * confirmation. It opens on a click and is gone under three seconds later, so
 * anything asking to be dismissed would take longer to read than the animation
 * takes to run.
 */
// Fades across 0.95 of its window rather than all of it, so the overlay is
// certainly at zero before it unmounts instead of landing on a floating-point
// boundary — the difference between a dissolve and a dissolve with a flicker.
export const OUTRO = 0.3;
export const outroAt = (t) => 1 - clamp01((t - (TOTAL - OUTRO)) / (OUTRO * 0.95));

/**
 * The folder is there from the START (Robert 2026-08-01), not conjured at the end.
 * It only has to arrive; a quick ease-out over a fraction of a second so it does
 * not simply pop into being.
 *
 * Showing it up front changes what the animation MEANS: you watch files going
 * somewhere you can already see, instead of watching them vanish and then being
 * told afterwards where they went.
 */
export function folderRevealAt(t) {
  // Not from the literal first frame — that frame is the untouched Files tab, and
  // a folder fading in over the real page looks like a rendering fault. It arrives
  // with the darkness, so by the time the cylinder is turning the destination is
  // already sitting there.
  return easeOut(clamp01((t - TIMELINE.lift * 0.45) / (TIMELINE.lift * 0.5)));
}

/**
 * How "live" the folder is: 0 while the helix is still winding up, 1 once it is
 * taking files. Drives the lift and the glow, so the folder visibly becomes the
 * destination at the moment things start arriving rather than sitting inert.
 */
export function folderActiveAt(t) {
  return easeOut(clamp01((t - WIND_END) / (TIMELINE.pour * 0.35)));
}
